import { useEffect, useState } from 'react';
import { useApiClient } from '../hooks/useApiClient';
import type { MaxFlowResponse } from '@metron/shared';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toErrorMessage } from '@metron/shared';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

export function MaxFlowPage() {
  const apiClient = useApiClient();
  const [data, setData] = useState<MaxFlowResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.get<MaxFlowResponse>('max-flow');
        setData(response);
      } catch (err) {
        setError(toErrorMessage(err, 'Failed to fetch max flow data'));
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [apiClient]);

  // Format chart data
  const chartData = data?.exceedingMetersByDay.map((item) => ({
    day: new Date(item.day).toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
    }),
    count: item.count,
  })) ?? [];

  // Format double capacity chart data
  const doubleCapacityChartData = data?.exceedingDoubleCapacityByDay.map((item) => ({
    day: new Date(item.day).toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
    }),
    count: item.count,
  })) ?? [];

  if (loading) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Maximum Operating Capacity Analysis</h1>
        <div className='flex items-center justify-center h-64'>
          <p className='text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Maximum Operating Capacity Analysis</h1>
        <Card>
          <CardContent className='py-8'>
            <p className='text-red-500'>Error: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8 space-y-6'>
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <h1 className='text-3xl font-bold'>Maximum Operating Capacity Analysis</h1>
          <p className='text-muted-foreground'>Safe operating capacity by meter size</p>
        </div>
        <p className='text-sm text-muted-foreground'>
          Note: These charts only consider installed meters. "Failure Analysis" customer is
          excluded.
        </p>
      </div>

      {/* Meters Exceeding Capacity Charts */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Chart - Exceeding Safe Capacity */}
        <Card>
          <CardHeader>
            <CardTitle>Meters Exceeding Safe Capacity</CardTitle>
            <CardDescription>
              Count of meters with daily max flow exceeding safe operating capacity (last 7 days)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.length > 0
              ? (
                <ResponsiveContainer width='100%' height={350}>
                  <BarChart data={chartData}>
                    <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                    <XAxis dataKey='day' tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload || !payload.length) return null;
                        const data = payload[0].payload;
                        return (
                          <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
                            <p className='font-semibold'>{data.day}</p>
                            <p className='text-sm'>
                              <span className='font-medium'>{data.count}</span>{' '}
                              meters exceeding capacity
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey='count' fill='#ef4444' name='Meters Exceeding Capacity' />
                  </BarChart>
                </ResponsiveContainer>
              )
              : (
                <div className='flex items-center justify-center h-[350px]'>
                  <p className='text-muted-foreground'>
                    No meters exceeded safe capacity in the last 7 days
                  </p>
                </div>
              )}
          </CardContent>
        </Card>

        {/* Chart - Exceeding 2x Capacity */}
        <Card>
          <CardHeader>
            <CardTitle>Meters Exceeding 2x Safe Capacity</CardTitle>
            <CardDescription>
              Count of meters with daily max flow exceeding 200% of safe operating capacity (last 7
              days)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {doubleCapacityChartData.length > 0
              ? (
                <ResponsiveContainer width='100%' height={350}>
                  <BarChart data={doubleCapacityChartData}>
                    <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                    <XAxis dataKey='day' tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload || !payload.length) return null;
                        const data = payload[0].payload;
                        return (
                          <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
                            <p className='font-semibold'>{data.day}</p>
                            <p className='text-sm'>
                              <span className='font-medium'>{data.count}</span>{' '}
                              meters exceeding 2x capacity
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey='count' fill='#dc2626' name='Meters Exceeding 2x Capacity' />
                  </BarChart>
                </ResponsiveContainer>
              )
              : (
                <div className='flex items-center justify-center h-[350px]'>
                  <p className='text-muted-foreground'>
                    No meters exceeded 2x safe capacity in the last 7 days
                  </p>
                </div>
              )}
          </CardContent>
        </Card>
      </div>

      {/* Tables Row */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Meter Size Capacities Table */}
        <Card>
          <CardHeader>
            <CardTitle>Safe Maximum Operating Capacity</CardTitle>
            <CardDescription>
              AWWA Cold-Water Meters specification at 100% accuracy (+/- 1.5%)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meter Size</TableHead>
                  <TableHead className='text-right'>Max Safe Flow (GPM)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.meterSizeCapacities.map((item) => (
                  <TableRow key={item.meterSize}>
                    <TableCell className='font-medium'>{item.meterSize}</TableCell>
                    <TableCell className='text-right'>
                      {item.maxFlowGpm.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Meter Details Table */}
        <Card>
          <CardHeader>
            <CardTitle>Meters Exceeding 2x Capacity (Last 24h)</CardTitle>
            <CardDescription>
              Individual meters that exceeded 200% of safe capacity yesterday
            </CardDescription>
          </CardHeader>
          <CardContent>
            {data?.exceedingDoubleCapacityMeters && data.exceedingDoubleCapacityMeters.length > 0
              ? (
                <div className='max-h-[350px] overflow-y-auto'>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Meter ID</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead className='text-right'>Safe (GPM)</TableHead>
                        <TableHead className='text-right'>Max Observed</TableHead>
                        <TableHead className='text-right'>Multiple</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.exceedingDoubleCapacityMeters.map((meter, i) => (
                        <TableRow key={meter.sensorId}>
                          <TableCell>
                            <a
                              href={`https://plus.waterscope.us/portfolio/${meter.customerId}/${meter.allSensorsGroupId}/${meter.allSensorsGroupId}/${meter.sensorId}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-blue-600 hover:underline'
                              // Rows without a meter ID would otherwise all share the link text
                              // "Unknown meter"; give each a distinct accessible name by position.
                              aria-label={meter.meterId
                                ? undefined
                                : `Unknown meter, row ${i + 1} — view in WaterScope Plus`}
                            >
                              {meter.meterId || 'Unknown meter'}
                            </a>
                          </TableCell>
                          <TableCell>{meter.meterSize || '-'}</TableCell>
                          <TableCell className='text-right'>
                            {meter.safeCapacity.toLocaleString()}
                          </TableCell>
                          <TableCell className='text-right'>
                            {meter.maxFlowObserved.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </TableCell>
                          <TableCell className='text-right font-medium text-red-600'>
                            {meter.capacityMultiple.toLocaleString(undefined, {
                              maximumFractionDigits: 1,
                            })}x
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )
              : (
                <div className='flex items-center justify-center h-[350px]'>
                  <p className='text-muted-foreground'>
                    No meters exceeded 2x safe capacity in the last 24 hours
                  </p>
                </div>
              )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
